if (!customElements.get('multicolumn-slider')) {
  customElements.define(
    'multicolumn-slider',
    class multicolumnSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        // Récupération des éléments
        this.swiper = this.querySelector('.swiper') || null;
        this.sliderControls = this.querySelector('.swiper-controls') || null;
        this.sliderPagination = this.querySelector('.swiper-pagination') || null;

        // Si pas de .swiper -> on arrête proprement
        if (!this.swiper) {
          console.warn('[multicolumn-slider] .swiper not found, aborting init.');
          return;
        }

        // Lecture dataset en toute sécurité
        const ds = this.swiper.dataset || {};
        this.enableSlider = (ds.enableSlider === 'true') || (ds.enableSlider === true);

        // Initialiser l’attribut selected-index si présent
        this.selectedIndex = this.selectedIndex;

        // Si le slider est désactivé, on sort (le ; à la fin de ton if le rendait inopérant)
        if (!this.enableSlider) return;

        this.initSlider();
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      initSlider() {
        // Modules additionnels
        const additionModules = [customTheme.Swiper.Autoplay, customTheme.Swiper.EffectFade];

        // Options avec garde-fous
        this.sliderOptions = {
          slidesPerView: 1,
          loop: true,
          effect: 'fade',
          fadeEffect: { crossFade: true },
          navigation: {
            nextEl: this.sliderControls?.querySelector('.swiper-button-next') || null,
            prevEl: this.sliderControls?.querySelector('.swiper-button-prev') || null,
          },
          pagination: {
            el: this.sliderPagination || null,
            clickable: true,
          },
          on: {
            init: this.handleAfterInit.bind(this),
          },
        };

        // Autoplay sécurisé
        const autoplayDelay = parseInt(this.swiper?.dataset?.autoplay || '0', 10) || 0;
        if (autoplayDelay > 0) {
          this.sliderOptions = {
            ...this.sliderOptions,
            autoplay: {
              delay: autoplayDelay,
              disableOnInteraction: false,
            },
          };
        }

        // Instanciation du carrousel si .swiper existe
        if (!window?.customTheme?.Carousel) {
          console.warn('[multicolumn-slider] customTheme.Carousel not available.');
          return;
        }

        this.sliderInstance = new window.customTheme.Carousel(this.swiper, this.sliderOptions, additionModules);
        this.sliderInstance.init();

        // Écouteurs sécurisés
        this.sliderInstance?.slider?.on('realIndexChange', this.handleSlideChange.bind(this));

        // Sélection de bloc en mode thème
        if (window.Shopify?.designMode) {
          document.addEventListener('shopify:block:select', (e) => {
            if (e.detail.sectionId !== this.dataset.sectionId) return;
            const target = e.target;
            const index = Number(target?.dataset?.index);
            if (!Number.isNaN(index)) {
              this.sliderInstance?.slider?.slideToLoop(index);
            }
          });
        }

        // Reset des animations hors slide actif
        if (this.sliderInstance?.slider) {
          const motionTextElements = Array.from(this.querySelectorAll('motion-element[data-text]'));
          motionTextElements.forEach((motionTextElement) => {
            if (
              typeof motionTextElement.resetAnimation === 'function' &&
              !motionTextElement.closest('.swiper-slide-active')
            ) {
              motionTextElement.resetAnimation();
            }
          });

          this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
          if (this.selectedElement) {
            this.onReady(this.selectedElement, this.sliderInstance.slider.slides);
          }

          const focusableElements = customTheme?.a11y?.getFocusableElements
            ? customTheme.a11y.getFocusableElements(this)
            : [];
          focusableElements.forEach((element) => {
            if (!element.classList.contains('swiper-button')) {
              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                if (slide) {
                  const idx = this.sliderInstance.slider.slides.indexOf(slide);
                  if (idx > -1) this.sliderInstance.slider.slideToLoop(idx);
                }
              });
            }
          });
        }
      }

      onReady(selectedElement) {
        if (!selectedElement) return;
        if (!customTheme?.config?.motionReduced) {
          const motionEls = selectedElement.querySelectorAll('motion-element');
          motionEls.forEach((motionEl) => {
            motionEl?.refreshAnimation?.();
          });
        }
      }

      handleAfterInit() {
        // Fix bullet active la 1ère fois, si contrôles présents
        if (!this.sliderControls) return;
        const activeBullet = this.sliderControls.querySelector('.swiper-pagination-bullet-active');
        if (activeBullet) {
          activeBullet.classList.remove('swiper-pagination-bullet-active');
          // Trigger reflow
          // eslint-disable-next-line no-unused-expressions
          activeBullet.offsetHeight;
          activeBullet.classList.add('swiper-pagination-bullet-active');
        }
      }

      handleSlideChange(swiper) {
        if (!swiper) return;
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex ?? this.selectedIndex;
        this.updateControlsScheme(slides?.[activeIndex]);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          const fromElements = this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`);
          const toElements = this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`);

          fromElements.forEach((fromElement) => {
            const motionEls = fromElement.querySelectorAll('motion-element');
            motionEls.forEach((motionEl) => {
              if (motionEl.hasAttribute('data-text')) {
                motionEl.resetAnimation?.();
              }
            });
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (toElement.classList.contains('swiper-slide-active')) {
                const motionEls = toElement.querySelectorAll('motion-element');
                motionEls.forEach((motionEl) => motionEl?.refreshAnimation?.());
              }
            });
          });
        }
      }

      updateControlsScheme(activeSlide) {
        if (!this.sliderControls || !activeSlide?.dataset?.colorScheme) return;
        const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
          className.startsWith('color-')
        );
        classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
        const colorScheme = activeSlide.dataset.colorScheme;
        if (colorScheme) this.sliderControls.classList.add(colorScheme);
      }
    }
  );
}
